import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/dealership/lib/api";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import LogActivityDialog from "@/dealership/components/LogActivityDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { stageStyle, fmtDate, fmtDateTime } from "@/dealership/lib/funnel";
import {
  Plus, Bell, Clock, CheckCircle2, AlarmClock, CalendarClock,
  Pencil, Trash2, User, ArrowRight,
} from "lucide-react";

export default function Activities() {
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const [editActivity, setEditActivity] = useState(null);

  const { data: notif = { overdue: [], today: [], upcoming: [], counts: {} } } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 60000,
  });
  const { data: feed = [] } = useQuery({
    queryKey: ["activities"],
    queryFn: async () => (await api.get("/activities")).data,
  });

  const invalidateAll = () => ["activities", "notifications", "notif-count", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const doneMut = useMutation({
    mutationFn: async (id) => api.post(`/activities/${id}/done`),
    onSuccess: () => { invalidateAll(); toast.success("Follow-up completed"); },
  });
  const snoozeMut = useMutation({
    mutationFn: async (id) => api.post(`/activities/${id}/snooze`),
    onSuccess: () => { invalidateAll(); toast.success("Snoozed to tomorrow"); },
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/activities/${id}`),
    onSuccess: () => { invalidateAll(); toast.success("Activity deleted"); },
  });

  const openNew = () => { setPreset(null); setEditActivity(null); setDialogOpen(true); };
  const logNext = (a) => { setPreset({ id: a.lead_id, name: a.lead_name }); setEditActivity(null); setDialogOpen(true); };
  const openEdit = (a) => { setEditActivity(a); setPreset(null); setDialogOpen(true); };

  const totalDue = (notif.counts?.overdue || 0) + (notif.counts?.today || 0);

  return (
    <div className="p-8">
      <PageHeader
        title="Activities"
        subtitle="Work every lead through the sales funnel with scheduled daily follow-ups."
        action={<Button onClick={openNew} data-testid="log-activity-button"><Plus className="h-4 w-4 mr-2" /> Log Activity</Button>}
      />

      <Tabs defaultValue="followups">
        <TabsList className="mb-6">
          <TabsTrigger value="followups" data-testid="tab-followups">
            <Bell className="h-4 w-4 mr-2" /> Follow-ups
            {totalDue > 0 && <span className="ml-2 bg-destructive text-white text-xs rounded-full px-1.5 py-0.5 font-mono-data">{totalDue}</span>}
          </TabsTrigger>
          <TabsTrigger value="feed" data-testid="tab-feed"><Clock className="h-4 w-4 mr-2" /> Activity Feed</TabsTrigger>
        </TabsList>

        <TabsContent value="followups">
          <div className="space-y-8">
            <FollowupGroup title="Overdue" icon={AlarmClock} color="text-destructive" items={notif.overdue}
              onDone={doneMut.mutate} onSnooze={snoozeMut.mutate} onLogNext={logNext} emptyText="Nothing overdue. Great job staying on top of it." />
            <FollowupGroup title="Due Today" icon={CalendarClock} color="text-amber-400" items={notif.today}
              onDone={doneMut.mutate} onSnooze={snoozeMut.mutate} onLogNext={logNext} emptyText="No follow-ups due today." />
            <FollowupGroup title="Upcoming" icon={Clock} color="text-muted-foreground" items={notif.upcoming}
              onDone={doneMut.mutate} onSnooze={snoozeMut.mutate} onLogNext={logNext} emptyText="No upcoming follow-ups scheduled." muted />
          </div>
        </TabsContent>

        <TabsContent value="feed">
          <Card className="divide-y divide-border">
            {feed.length === 0 ? (
              <div className="text-center py-14 text-muted-foreground">No activities logged yet. Log your first touchpoint.</div>
            ) : (
              feed.map((a) => (
                <div key={a.id} className="flex items-start gap-4 p-4" data-testid={`activity-${a.id}`}>
                  <div className="mt-1">
                    <Badge className={`${stageStyle[a.type] || "bg-zinc-500/12 text-zinc-400"} border-0`}>{a.type}</Badge>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{a.lead_name}</p>
                    {a.notes && <p className="text-sm text-muted-foreground mt-0.5 break-words">{a.notes}</p>}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{a.assignee_name}</span>
                      <span>{fmtDateTime(a.created_at)}</span>
                      {a.follow_up_date && !a.follow_up_done && <span className="flex items-center gap-1 text-primary"><CalendarClock className="h-3 w-3" />Follow-up {fmtDate(a.follow_up_date)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-activity-${a.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => delMut.mutate(a.id)} data-testid={`delete-activity-${a.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              ))
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <LogActivityDialog open={dialogOpen} onOpenChange={setDialogOpen} presetLead={preset} activity={editActivity} />
    </div>
  );
}

function FollowupGroup({ title, icon: Icon, color, items, onDone, onSnooze, onLogNext, emptyText, muted }) {
  return (
    <div>
      <div className={`flex items-center gap-2 mb-3 ${color}`}>
        <Icon className="h-4 w-4" />
        <h3 className="font-heading font-bold">{title}</h3>
        <span className="text-xs font-mono-data">({items.length})</span>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground pl-6">{emptyText}</p>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => (
            <Card key={a.id} className={`p-4 flex items-center gap-4 ${muted ? "" : "border-l-4"} ${!muted && color === "text-destructive" ? "border-l-destructive" : !muted ? "border-l-amber-500" : ""}`} data-testid={`followup-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{a.lead_name}</span>
                  <Badge className={`${stageStyle[a.type] || "bg-zinc-500/12 text-zinc-400"} border-0 text-xs`}>{a.type}</Badge>
                </div>
                {a.notes && <p className="text-sm text-muted-foreground mt-1 truncate">{a.notes}</p>}
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" />Due {fmtDate(a.follow_up_date)}</span>
                  <span className="flex items-center gap-1"><User className="h-3 w-3" />{a.assignee_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => onLogNext(a)} data-testid={`lognext-${a.id}`}>
                  Log next <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onSnooze(a.id)} data-testid={`snooze-${a.id}`}>Snooze</Button>
                <Button variant="ghost" size="icon" onClick={() => onDone(a.id)} data-testid={`done-${a.id}`} title="Mark done">
                  <CheckCircle2 className="h-5 w-5 text-green-400" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
